<template>
  <div class="sys-page">
    <div class="toolbar">
      <div>
        <h3>菜单管理</h3>
        <p class="hierarchy-hint">层级规则：菜单 → 子菜单 / 按钮 / 接口，按钮 → 接口，接口不可再添加子项</p>
      </div>
      <el-button v-perm="'system:menu:save'" type="primary" size="small" @click="openEdit(null, 0)">新增顶级菜单</el-button>
    </div>

    <el-table :data="menuTree" v-loading="loading" row-key="id" border stripe size="small"
              :tree-props="{ children: 'children' }">
      <el-table-column prop="name" label="名称" min-width="220" />
      <el-table-column label="类型" width="90">
        <template slot-scope="s">
          <el-tag size="mini" :type="tagType(nodeType(s.row))">{{ typeLabel(nodeType(s.row)) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="页面 / 接口路径" min-width="260">
        <template slot-scope="s">
          <span v-if="nodeType(s.row) === NODE_TYPE.API" class="api-path">
            <el-tag size="mini" type="info">{{ s.row.apiMethod || "*" }}</el-tag>
            {{ s.row.apiPath }}
          </span>
          <span v-else-if="nodeType(s.row) === NODE_TYPE.MENU">{{ s.row.path || "-" }}</span>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column prop="component" label="组件" min-width="180" />
      <el-table-column prop="perms" label="权限码" min-width="190" />
      <el-table-column prop="orderNum" label="排序" width="70" />
      <el-table-column label="状态" width="70">
        <template slot-scope="s">{{ s.row.status === 1 ? "启用" : "禁用" }}</template>
      </el-table-column>
      <el-table-column label="操作" width="250" fixed="right">
        <template slot-scope="s">
          <el-button v-if="canAddChild(s.row)" v-perm="'system:menu:save'" size="mini"
                     @click="openEdit(null, s.row.id)">{{ addChildLabel(s.row) }}</el-button>
          <el-button v-perm="'system:menu:save'" size="mini" @click="openEdit(s.row)">编辑</el-button>
          <el-button v-perm="'system:menu:delete'" size="mini" type="danger" plain @click="removeMenu(s.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog :title="editing.id ? '编辑节点' : '新增节点'" :visible.sync="editVisible" width="560px">
      <el-form :model="editing" label-width="100px" size="small">
        <el-form-item label="上级节点">
          <el-select v-model="editing.parentId" filterable style="width: 100%" placeholder="请选择上级节点">
            <el-option v-if="canUseRoot" :value="0" label="根节点" />
            <el-option v-for="m in parentOptions" :key="m.id" :value="m.id" :label="m.label" />
          </el-select>
        </el-form-item>
        <el-form-item label="类型">
          <el-radio-group v-model="editing.nodeType" @change="handleTypeChange">
            <el-radio v-for="item in typeOptions" :key="item.value" :label="item.value"
                      :disabled="item.disabled">{{ item.label }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="名称"><el-input v-model="editing.name" /></el-form-item>

        <template v-if="editing.nodeType === NODE_TYPE.MENU">
          <el-form-item label="页面路径">
            <el-input v-model="editing.path" placeholder="分组菜单可留空，例如 /client/system/user" />
          </el-form-item>
          <el-form-item label="页面组件">
            <el-input v-model="editing.component" placeholder="分组菜单可留空，例如 system/UserMgmt" />
          </el-form-item>
          <el-form-item label="图标"><el-input v-model="editing.icon" /></el-form-item>
        </template>

        <template v-if="editing.nodeType === NODE_TYPE.API">
          <el-form-item label="接口路径" required>
            <el-input v-model="editing.apiPath" placeholder="Ant 路径，例如 /system/menu/delete/**" />
          </el-form-item>
          <el-form-item label="请求方法" required>
            <el-select v-model="editing.apiMethod" style="width: 100%">
              <el-option v-for="method in apiMethods" :key="method" :label="method" :value="method" />
            </el-select>
          </el-form-item>
        </template>

        <el-form-item label="权限码">
          <el-input v-model="editing.perms" :placeholder="permPlaceholder" />
        </el-form-item>
        <el-form-item label="排序"><el-input-number v-model="editing.orderNum" :min="0" /></el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="editing.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="editVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="saving" @click="saveMenu">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { systemModules } from "@r";
import {
  NODE_TYPE,
  NODE_TYPE_LABEL,
  canHaveChildren,
  canUseParent,
  getNodeType,
  toMenuPayload,
} from "./menuModel";

function buildTree(flat) {
  const byId = {};
  flat.forEach(n => { byId[n.id] = { ...n, children: [] }; });
  const roots = [];
  Object.values(byId).forEach(n => {
    if (n.parentId && byId[n.parentId]) byId[n.parentId].children.push(n);
    else roots.push(n);
  });
  const sortRec = arr => {
    arr.sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0));
    arr.forEach(n => n.children && sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

export default {
  data() {
    return {
      NODE_TYPE,
      loading: false,
      saving: false,
      flat: [],
      menuTree: [],
      editVisible: false,
      editing: this.blank(),
      apiMethods: ["*", "GET", "POST", "PUT", "DELETE", "PATCH"],
    };
  },
  computed: {
    canUseRoot() {
      return canUseParent(this.editing.nodeType, null);
    },
    parentOptions() {
      const excluded = this.excludedParentIds(this.editing.id);
      const indent = new Map();
      const walk = (nodes, depth) => {
        nodes.forEach(n => {
          indent.set(n.id, depth);
          if (n.children && n.children.length) walk(n.children, depth + 1);
        });
      };
      walk(this.menuTree, 0);
      return this.flat
        .filter(n => !excluded.has(n.id) && canUseParent(this.editing.nodeType, n))
        .map(n => ({
          id: n.id,
          label: `${"— ".repeat(indent.get(n.id) || 0)}${n.name}（${this.typeLabel(this.nodeType(n))}）`,
        }));
    },
    typeOptions() {
      const parent = this.parentById(this.editing.parentId);
      return [NODE_TYPE.MENU, NODE_TYPE.BUTTON, NODE_TYPE.API].map(value => ({
        value,
        label: NODE_TYPE_LABEL[value],
        disabled: !canUseParent(value, parent),
      }));
    },
    permPlaceholder() {
      if (this.editing.nodeType === NODE_TYPE.MENU) return "例如 system:user:list";
      if (this.editing.nodeType === NODE_TYPE.BUTTON) return "按钮权限，例如 system:user:save";
      return "调用接口所需权限，通常与所属按钮一致";
    },
  },
  created() { this.load(); },
  methods: {
    blank() {
      return {
        id: null,
        parentId: 0,
        name: "",
        path: "",
        component: "",
        perms: "",
        nodeType: NODE_TYPE.MENU,
        storageMenuType: "MENU",
        icon: "",
        orderNum: 0,
        status: 1,
        apiPath: "",
        apiMethod: "*",
      };
    },
    nodeType(row) { return getNodeType(row); },
    typeLabel(type) { return NODE_TYPE_LABEL[type] || type; },
    tagType(type) {
      if (type === NODE_TYPE.BUTTON) return "warning";
      if (type === NODE_TYPE.API) return "success";
      return "";
    },
    parentById(id) {
      if (!id) return null;
      return this.flat.find(n => n.id === id) || null;
    },
    excludedParentIds(id) {
      const excluded = new Set();
      if (!id) return excluded;
      excluded.add(id);
      const collect = parentId => {
        this.flat.filter(n => n.parentId === parentId).forEach(n => {
          if (!excluded.has(n.id)) {
            excluded.add(n.id);
            collect(n.id);
          }
        });
      };
      collect(id);
      return excluded;
    },
    canAddChild(row) { return canHaveChildren(row); },
    addChildLabel(row) {
      return this.nodeType(row) === NODE_TYPE.BUTTON ? "新增接口" : "新增子项";
    },
    load() {
      this.loading = true;
      systemModules.listMenus()
        .then(res => {
          this.flat = res.data || [];
          this.menuTree = buildTree(this.flat);
        })
        .catch(e => this.$message.error(e.message || "加载失败"))
        .finally(() => { this.loading = false; });
    },
    openEdit(row, parentId) {
      if (row) {
        const { children, ...fields } = row;
        this.editing = {
          ...this.blank(),
          ...fields,
          nodeType: this.nodeType(row),
          storageMenuType: row.menuType,
          apiMethod: row.apiMethod || "*",
        };
      } else {
        const parent = this.parentById(parentId);
        const nodeType = parent && this.nodeType(parent) === NODE_TYPE.BUTTON
          ? NODE_TYPE.API
          : NODE_TYPE.MENU;
        this.editing = { ...this.blank(), parentId: parentId || 0, nodeType };
      }
      this.editVisible = true;
    },
    handleTypeChange() {
      const parent = this.parentById(this.editing.parentId);
      if (!canUseParent(this.editing.nodeType, parent)) {
        if (this.editing.nodeType === NODE_TYPE.MENU) {
          this.editing.parentId = 0;
        } else {
          const first = this.parentOptions[0];
          this.editing.parentId = first ? first.id : null;
        }
      }
    },
    validateEditing() {
      const name = String(this.editing.name || "").trim();
      if (!name) return "请输入名称";
      const parent = this.parentById(this.editing.parentId);
      if (!canUseParent(this.editing.nodeType, parent)) return "当前上级节点不允许添加该类型";
      if (this.editing.nodeType === NODE_TYPE.API && !String(this.editing.apiPath || "").trim()) {
        return "请输入接口路径";
      }
      if (this.editing.nodeType === NODE_TYPE.API && !this.editing.apiMethod) return "请选择请求方法";
      if (this.editing.nodeType === NODE_TYPE.MENU) {
        const hasPath = Boolean(String(this.editing.path || "").trim());
        const hasComponent = Boolean(String(this.editing.component || "").trim());
        if (hasComponent && !hasPath) return "填写页面组件时必须同时填写页面路径";
      }
      const directChildren = this.flat.filter(n => n.parentId === this.editing.id);
      const proposedParent = { menuType: this.editing.nodeType, apiPath: this.editing.nodeType === NODE_TYPE.API ? "_" : "" };
      if (directChildren.some(child => !canUseParent(this.nodeType(child), proposedParent))) {
        return "该节点已有不符合新类型层级的子项，请先调整子项";
      }
      return "";
    },
    saveMenu() {
      const invalidReason = this.validateEditing();
      if (invalidReason) {
        this.$message.warning(invalidReason);
        return;
      }
      this.saving = true;
      const payload = toMenuPayload({
        ...this.editing,
        name: String(this.editing.name).trim(),
        path: String(this.editing.path || "").trim() || null,
        component: String(this.editing.component || "").trim() || null,
        perms: String(this.editing.perms || "").trim() || null,
        apiPath: String(this.editing.apiPath || "").trim() || null,
      });
      systemModules.saveMenu(payload)
        .then(() => { this.$message.success("保存成功"); this.editVisible = false; this.load(); })
        .catch(e => this.$message.error(e.message || "保存失败"))
        .finally(() => { this.saving = false; });
    },
    removeMenu(row) {
      if (this.flat.some(n => n.parentId === row.id)) {
        this.$message.warning("该节点还有子项，请先移动或删除子项");
        return;
      }
      this.$confirm(`确认删除${this.typeLabel(this.nodeType(row))}“${row.name}”？`, "提示", { type: "warning" })
        .then(() => systemModules.deleteMenu(row.id))
        .then(() => { this.$message.success("已删除"); this.load(); })
        .catch(e => e !== "cancel" && this.$message.error((e && e.message) || "删除失败"));
    },
  },
};
</script>

<style scoped>
.sys-page { padding: 16px; background: #fff; min-height: 100%; }
.toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.toolbar h3 { margin: 0; }
.hierarchy-hint { margin: 6px 0 0; color: #909399; font-size: 12px; }
.api-path .el-tag { margin-right: 6px; }
</style>
